import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GatewayClusterService, GatewayFormsService } from '@netfoundry-ui/shared/gateway';
import { Collector, Endpoint, Exports, GatewayCluster } from '@netfoundry-ui/shared/model';
import { CollectorService, ExportersService, ExportsService } from '@netfoundry-ui/shared/netflow';
import {
    FeatureService,
    LoggerService,
    NetworkGroupService,
    RegionService,
    ZitiEnabledService,
} from '@netfoundry-ui/shared/services';
import { RegionTransformPipe } from '@netfoundry-ui/ui/pipes';
import { Subscription } from 'rxjs';

const gatewayType = 'GCPCPEGW';
const exporterType = 'dvn-gateway';

@Component({
    selector: 'app-google-cloud-gateway-form',
    templateUrl: './google-cloud-gateway-form.component.html',
    styleUrls: ['./google-cloud-gateway-form.component.scss', '../gatewayform.component.scss'],
    standalone: false
})
export class GoogleCloudGatewayFormComponent implements OnInit, OnDestroy {
    model: Endpoint = new Endpoint({});
    public clusterModel = new GatewayCluster({});

    @Input() isInline = false;
    @Input() public currentOrgId;
    @Input() public currentNetworkId;
    @Input() showBreakoutFields = false;
    @Input() hideHaUpgrade = false;
    @Output() back: EventEmitter<boolean> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() newGateway: EventEmitter<Endpoint> = new EventEmitter();

    gatewayTypes = [];
    protectionTypes = [];

    isComplete = false;
    regions: Location[] = [];
    errorName = false;
    errorNameLength = false;
    errorType = false;
    errorLocation = false;
    errorRegion = false;
    errorNextHop = false;
    errorCollector = false;
    errorFrequency = false;
    gateways = [];
    regKey = [];
    attempt = 0;
    isLoading = false;

    isHAGateway = false;

    netflowEnabled = false;

    exportModel = new Exports({});

    url = '';

    collectors = [];
    isBuilding = true;
    provisionedString = 'has been created';
    buildingString = 'is building';
    completedTitleStatusString = this.buildingString;
    timeout = 150;
    processing = false;
    hideHelp = false;
    errors = {
        name: false,
        geoRegionId: false,
        azureSiteName: false,
        azureResourceGroupName: false,
        azureVirtualWanId: false,
        publicIpdAddress: false,
        bgpPeeringAddress: false,
        bpgASN: false,
        bgpPeerWeight: false,
        deviceLinkSpeed: false,
        deviceVendor: false,
        deviceModel: false,
        bpgNeighborASN: false,
        bgpPeeringNeighborAddress: false,
    };
    maxLength = '64';
    private updatedRegKeySub = new Subscription();
    private subscription = new Subscription();

    constructor(
        private logger: LoggerService,
        private regionService: RegionService,
        public featureService: FeatureService,
        private clusterService: GatewayClusterService,
        public gatewayFormService: GatewayFormsService,
        public authorizationService: AuthorizationService,
        private collectorService: CollectorService,
        private exportersService: ExportersService,
        private exportsService: ExportsService,
        private organizationService: NetworkGroupService,
        public zitiEnabledService: ZitiEnabledService,
        private regionTransform: RegionTransformPipe
    ) {
        this.exportModel.exporterType = exporterType;
        this.exportModel.exportIntervalSeconds = 20;
    }

    ngOnInit() {
        this.processing = false;
        this.errorName = false;
        this.errorLocation = false;
        this.errorRegion = false;
        this.errorType = false;

        // geo region for gateways
        this.subscription.add(
            this.regionService.get().subscribe((result) => {
                this.regions = this.regionTransform.transform(
                    result as Location[],
                    false,
                    false,
                    true,
                    false,
                    false,
                    false
                );
            })
        );

        // Reg Key
        // subscribe to changes to updatedRegKey
        this.updatedRegKeySub.add(
            this.gatewayFormService.updatedRegKey.subscribe((isUpdated) => {
                this.regKey = this.gatewayFormService.regKey;
                this.gateways = this.gatewayFormService.gateways;
                // if the registration key was not obtained and there were less than 10 attempts
                if (!isUpdated) {
                    this.attempt++;
                    // setting a 250ms timeout before calling loadGateway again
                    setTimeout(() => {
                        if (this.isHAGateway) this.gatewayFormService.loadGatewayCluster(this.clusterModel);
                        else this.gatewayFormService.loadGateway(this.model);
                    }, this.gatewayFormService.timeout);
                } else {
                    this.completedTitleStatusString = this.provisionedString;
                    this.isBuilding = false;
                }
            })
        );

        if (this.authorizationService.canListNetflowCollectors() && this.authorizationService.canCreateExports()) {
            if (this.authorizationService.canListOrganizations()) {
                this.subscription.add(
                    this.organizationService.get().subscribe((res) => {
                        if (res.length > 1) {
                            this.getCollectors();
                        }
                    })
                );
                this.getCollectors({ networkGroupIdsIn: [this.currentOrgId] });
            } else {
                this.getCollectors({ networkGroupIdsIn: [this.currentOrgId] });
            }
        }

        this.protectionTypes = this.clusterService.getProtectionTypes();
    }

    getCollectors(params?) {
        this.subscription.add(
            this.collectorService.find(params).subscribe((result) => {
                this.collectors = result as Collector[];
            })
        );
    }

    ngOnDestroy() {
        this.updatedRegKeySub.unsubscribe();
        this.subscription.unsubscribe();
    }

    async save() {
        this.logger.info('Saving...');
        if (this.validate()) {
            this.processing = true;
            this.model.endpointType = gatewayType;

            if (this.isHAGateway) {
                const result = await this.gatewayFormService.saveHa(this.clusterModel, this.model);
                this.processing = false;
                if (result != null) {
                    this.clusterModel = new GatewayCluster(result);
                    this.model = new Endpoint(result);
                    this.isComplete = true;

                    // setting the title to indicate that the gateways are building
                    this.completedTitleStatusString = this.buildingString;
                    this.gatewayFormService.loadGatewayCluster(this.clusterModel);
                }
            } else {
                const result = await this.gatewayFormService.save(this.model);

                this.processing = false;
                if (result != null) {
                    this.model = result;

                    this.completedTitleStatusString = this.buildingString;
                    this.newGateway.emit(new Endpoint(this.model));

                    if (!this.netflowEnabled) {
                        this.isComplete = true;
                    } else {
                        this.subscription.add(
                            this.exportersService
                                .findByNativeId(exporterType, this.model.id, {
                                    networkId: this.currentNetworkId,
                                })
                                .subscribe((result) => {
                                    this.exportModel.exporterKey = result.key;
                                    this.exportModel.networkGroupId = this.currentOrgId;
                                    this.exportModel.name = this.model.name;

                                    this.subscription.add(
                                        this.exportsService.save(this.exportModel).subscribe((result) => {
                                            this.isComplete = true;
                                        })
                                    );
                                })
                        );
                    }
                    // attempting to get the registration key for the gateway
                    this.gatewayFormService.loadGateway(this.model);
                }
            }
        } else {
            this.logger.info('Gateway form is invalid');
        }
    }

    share() {
        // TODO fix me
        this.gatewayFormService.share(this.gateways[0]);
    }

    goBack() {
        this.back.emit(true);
    }

    hideForm(response?: unknown) {
        this.hide.emit(response);
    }

    validate(): boolean {
        let exportForValidation;
        if (this.netflowEnabled) {
            exportForValidation = this.exportModel;
        }

        const validateResult = this.gatewayFormService.validate(
            this.model,
            this.isHAGateway,
            gatewayType,
            undefined,
            exportForValidation
        );

        this.errorName = validateResult.errorName;
        this.errorLocation = validateResult.errorLocation;
        this.errorType = validateResult.errorType;
        this.errorRegion = validateResult.errorRegion;
        this.errorNameLength = validateResult.errorNameLength;
        this.errorNextHop = validateResult.errorNextHop;
        this.errorCollector = validateResult.errorCollector;
        this.errorFrequency = validateResult.errorFrequency;

        return validateResult.isValid;
    }

    toggleHAGateway() {
        this.isHAGateway = !this.isHAGateway;
        if (this.isHAGateway) {
            this.maxLength = '59';
            this.model.name = this.model.name.substring(0, 59);
        } else {
            this.maxLength = '64';
        }
    }

    toggleNetflowEnabled() {
        this.netflowEnabled = !this.netflowEnabled;
    }

    downloadJWT(model, index) {
        this.gatewayFormService.downloadJWT(model, index);
    }
}
